import type { BrushStroke } from "./bishun-drawing.js";

/**
 * Writing the strokes out, one after another.
 *
 * Apart from `bishun-drawing.ts`, which builds the SVG: this is how much of a
 * stroke is written at a given moment, how long it takes and what happens next.
 *
 * **The animation is CSS and the sequencing is here.** Drawing a stroke is a
 * `stroke-dashoffset` going to zero under a transition `modules/cidian-bishun`
 * declares, so what this does per stroke is set a duration, set an offset and
 * wait. That is a great deal less code than driving frames, and it leaves the
 * drawing to the part of the browser that is good at it.
 */

/**
 * How far past the median the dash runs, in the grid's own units.
 *
 * A brush is as wide as the widest stroke and its cap is round, so the ink
 * reaches half a width beyond each end of the line it follows. The dash has to
 * cover that or the last of a stroke stays undrawn, and a whole width covers
 * both ends with room to spare.
 */
const brushOverdraw = 128;

/**
 * How much of a stroke is still to be written, as the dash offset for it.
 *
 * The dash is the whole stroke and the offset slides it out of the way, so an
 * offset equal to the length is a stroke not yet started and zero is one
 * finished. Both are set here rather than in the stylesheet because only this
 * knows how long the stroke is.
 */
export function setBrush(stroke: BrushStroke, written: boolean): void {
  const drawn = stroke.length + brushOverdraw;

  stroke.brush.setAttribute("stroke-dasharray", String(drawn));
  stroke.brush.setAttribute("stroke-dashoffset", written ? "0" : String(drawn));
}

/**
 * How fast the brush moves, in grid units per millisecond.
 *
 * A long horizontal stroke is around 700 units, so it takes about half a
 * second, and a dot takes the minimum below. Slow enough to follow with a pen
 * in hand, which is what a reader on this block is doing.
 */
const unitsPerMillisecond = 1.5;

/** The least time a stroke takes, however short it is. */
const shortestStroke = 220;

/** The pause between one stroke finishing and the next starting. */
const betweenStrokes = 110;

/** How long a stroke of that length takes to write. */
export function strokeMilliseconds(length: number): number {
  return Math.max(shortestStroke, Math.round(length / unitsPerMillisecond));
}

/**
 * Write the character out, one stroke after another.
 *
 * Every stroke is put back to unwritten first and the drawing forced to settle
 * there before any of them is asked to move, or the browser coalesces the two
 * changes and the whole character appears at once. Reading `getBoundingClientRect`
 * is what forces it, and it is the smallest thing that does.
 *
 * The handle is what a second press cancels the first run with. Without it two
 * presses leave two sequences writing the same strokes at different speeds.
 */
export function playStrokes(strokes: readonly BrushStroke[]): () => void {
  const timers: ReturnType<typeof setTimeout>[] = [];
  let at = 0;

  for (const stroke of strokes) {
    stroke.brush.style.setProperty("--cb-stroke-duration", "0ms");
    setBrush(stroke, false);
  }

  strokes[0]?.brush.getBoundingClientRect();

  const writeNext = (): void => {
    const stroke = strokes[at];

    if (stroke === undefined) {
      return;
    }

    const duration = strokeMilliseconds(stroke.length);

    stroke.brush.style.setProperty(
      "--cb-stroke-duration",
      `${String(duration)}ms`,
    );
    setBrush(stroke, true);
    at += 1;
    timers.push(setTimeout(writeNext, duration + betweenStrokes));
  };

  writeNext();

  return (): void => {
    for (const timer of timers) {
      clearTimeout(timer);
    }
  };
}
